import { SerializedData } from 'bernie-core';
import { AnalyticsStore } from 'bernie-plugin-mobx';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import { RequestOptions } from 'bernie-graphql';

import {
  FlightsShoppingPwaFlightSearchFormLoaded,
  FlightsShoppingPwaFlightSearchFormLoading
} from '__generated__/api/';
import { clientInfoHeader } from 'utility/header-utils';
import { SearchFormStore } from '.';
import { SearchCriteriaStore } from './search-criteria-store';
import analyticsWrapper from 'utility/analytics-wrapper';
import FlightsSearchFormType = FlightsShoppingPwaFlightSearchFormLoaded.FlightsLoadedSearchFormInlineFragment;
import { BaseBexApiStore } from './bex-api-store';

export class SearchFormResponseStore extends BaseBexApiStore {
  private response: FlightsShoppingPwaFlightSearchFormLoaded.FlightsSearch = {
    __typename: 'FlightsSearchResponse'
  };
  private needSearch: boolean;

  public hydrate(data: SerializedData): void {
    super.hydrate(data);

    this.bexApiSource.clientConfigs.bexApiUrl = '/graphql';
  }

  public async fetchResults(variables: FlightsShoppingPwaFlightSearchFormLoaded.Variables, options: RequestOptions) {
    const { appName, region } = this.bexApiSource.clientConfigs;
    this.bexApiSource.headers = clientInfoHeader(this.bexApiSource.headers, appName, region);
    this.setNeedFetch(false);

    return await this.bexApiSource.operations
      .flightsShoppingPwaFlightSearchFormLoaded(variables, options)
      .then(response => {
        const { flightsSearch } = response.data;

        this.response = {
          ...flightsSearch
        };

        return {
          ...flightsSearch
        };
      })
      .catch(error => {
        this.logger.logEvent(new SystemEvent(SystemEventLevel.CRITICAL, 'FETCH_SEARCH_FORM_LOADED'), error);
        this.response = {
          __typename: 'FlightsSearchResponse'
        };

        return null;
      });
  }

  public async fetchLoading(
    searchVariables: FlightsShoppingPwaFlightSearchFormLoading.Variables,
    options: RequestOptions,
    analytics: AnalyticsStore
  ) {
    this.logger.logEvent(
      new SystemEvent(SystemEventLevel.INFO, 'FETCH_SEARCH_FORM_VARIABLES'),
      JSON.stringify(searchVariables)
    );

    this.logger.logEvent(new SystemEvent(SystemEventLevel.INFO, 'FETCH_SEARCH_FORM_OPTIONS'), JSON.stringify(options));

    await this.bexApiSource.operations
      .flightsShoppingPwaFlightSearchFormLoading(searchVariables, options)
      .then(response => {
        this.logger.logEvent(
          new SystemEvent(SystemEventLevel.INFO, 'FETCH_SEARCH_FORM_RESPONSE'),
          JSON.stringify(response)
        );

        const { flightsSearch } = response.data;

        analyticsWrapper.publishAnalyticsPayload(response, analytics);

        this.response = {
          ...flightsSearch
        };
      })
      .catch(error => {
        console.error(error);
        this.logger.logEvent(new SystemEvent(SystemEventLevel.CRITICAL, 'FETCH_SEARCH_FORM_LOADING'), error);
      });
  }

  public preFetch(searchFormStore: SearchFormStore) {
    searchFormStore.clearErrorRef();
  }

  public postFetch(
    searchFormStore: SearchFormStore,
    searchCriteriaStore: SearchCriteriaStore,
    state: 'LOADING' | 'LOADED'
  ) {
    const searchForm = this.response.searchForm;

    searchFormStore.clearErrorRef();
    searchFormStore.validateAndRestrict(searchForm as FlightsSearchFormType);
    searchFormStore.hydrate({ searchForm });

    if (state === 'LOADED') {
      searchCriteriaStore.syncWithSearchFormStore(searchFormStore);
      searchFormStore.loading = false;
      searchFormStore.setSearchFormInitialState();
      this.setNeedFetch(false);
    }
  }

  public failedFetch(searchFormStore: SearchFormStore) {
    searchFormStore.loading = false;
  }

  public setNeedFetch(needSearch: boolean) {
    this.needSearch = needSearch;
  }

  public shouldFetch() {
    return this.needSearch;
  }
}

SearchFormResponseStore.DEFAULTS = {
  needSearch: true
};
